import { getAllTrackingCookies, getSessionId } from "./utils";

// ---------------------------------------------------------------------------
// Conversion detection & reporting
// ---------------------------------------------------------------------------

/**
 * Detect and report a conversion event on the current page.
 *
 * Host pages (thank-you / CV pages) set:
 *   window.cbcvConvertInfo = ["converted", { application_number: "xxx" }];
 *
 * This function reads that value, combines it with tracking cookies,
 * and sends a fire-and-forget POST to the conversions API.
 *
 * After sending, `window.cbcvConvertInfo` is deleted to prevent
 * duplicate submissions on SPA navigations or re-renders.
 */
export function detectAndReportConversion(logApiBase: string): void {
  const convertInfo = window.cbcvConvertInfo;
  if (!convertInfo) return;

  const [conversionType, conversionData] = convertInfo;
  if (!conversionType) return;

  const cookies = getAllTrackingCookies();
  const sessionId = getSessionId();

  const payload = {
    session_id: sessionId,
    url: window.location.href,
    user_agent: navigator.userAgent,
    luid: cookies.lr_luid || null,
    conversion_type: conversionType,
    conversion_data: conversionData || null,
    popup_config_id: cookies.lr_popup_config_id
      ? parseInt(cookies.lr_popup_config_id, 10)
      : null,
    scenario_id: cookies.lr_scenario_id || null,
    node_id: cookies.lr_node_id || null,
  };

  // Fire-and-forget — must never block the thank-you page
  const url = `${logApiBase}/api/v1/conversions`;

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, JSON.stringify(payload));
  } else {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Intentionally swallowed — fire-and-forget
    });
  }

  // Prevent duplicate submission
  delete window.cbcvConvertInfo;
}
